import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CalendarCheck, Phone, Award, GraduationCap, BadgeCheck } from "lucide-react";
import { MaskedLines, Reveal, FadeIn } from "../components/motion";
import { PhotoSlot } from "../components/PhotoSlot";
import { SectionHead } from "../components/SectionHead";
import { Marquee } from "../components/Marquee";
import { RetinaServices } from "../components/RetinaServices";
import { CataractServices } from "../components/CataractServices";
import { GalleryGrid } from "../components/GalleryGrid";
import {
  Specialties, OtherServices, WhyChoose, PatientJourney, BookSection, MapSection, TrustStrip,
} from "../components/sections";
import { CLINIC, DOCTOR } from "../lib/constants";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 50]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-white" data-testid="hero-section">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(50% 55% at 85% 20%, rgba(37,84,235,0.08), transparent 70%), radial-gradient(40% 45% at 10% 90%, rgba(6,182,212,0.06), transparent 70%)",
        }}
      />
      <div className="relative container-x pt-12 pb-10 sm:pt-16 lg:pt-20 lg:pb-0">
        <div className="grid gap-12 lg:grid-cols-[1.15fr_0.85fr] items-center">
          <motion.div style={{ y: yText }} className="pb-6 lg:pb-28">
            <FadeIn>
              <span className="inline-flex items-center gap-2 rounded-full border border-aqua/30 bg-aqua-mist px-4 py-1.5 text-[11px] sm:text-xs font-bold uppercase tracking-[0.18em] text-aqua" data-testid="hero-badge">
                <BadgeCheck className="w-3.5 h-3.5" aria-hidden="true" /> Retina & Cataract Specialist
              </span>
            </FadeIn>
            <h1 className="mt-6 text-[2.7rem] leading-[1.02] sm:text-6xl lg:text-[4.6rem] font-extrabold tracking-tight text-ink" data-testid="hero-heading">
              <MaskedLines
                lines={[
                  <>World Class</>,
                  <>
                    Retina <span className="font-serif italic font-semibold text-brand-bright">&</span> Cataract
                  </>,
                  <>
                    Care<span className="text-aqua">.</span>
                  </>,
                ]}
              />
            </h1>
            <FadeIn delay={0.55}>
              <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600">
                Advanced eye care backed by specialist expertise, modern diagnostic technology and a patient-first
                approach.
              </p>
              <p className="mt-4 flex items-center gap-2 text-sm font-bold text-ink">
                <GraduationCap className="w-4 h-4 text-aqua" aria-hidden="true" />
                {DOCTOR.name} · {DOCTOR.degrees}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contact#book" className="btn-primary" data-testid="hero-book-button">
                  <CalendarCheck className="w-4 h-4" aria-hidden="true" /> Book an Appointment
                </Link>
                <a href={CLINIC.phoneLink} className="btn-outline" data-testid="hero-call-button">
                  <Phone className="w-4 h-4" aria-hidden="true" /> Call {CLINIC.phoneDisplay.replace(/\s/g, "")}
                </a>
              </div>
            </FadeIn>
          </motion.div>

          <motion.div style={{ y: yImg }} className="relative lg:pb-20">
            <FadeIn delay={0.35}>
              <div className="relative mx-auto max-w-sm lg:max-w-none">
                <div className="absolute -inset-4 rounded-t-[999px] rounded-b-[2rem] bg-brand-mist" aria-hidden="true" />
                <PhotoSlot
                  dark
                  label="Dr. S.J. Vikas"
                  note="MBBS, MD, FAICO · Retina & Cataract Specialist"
                  src="/images/doctor-portrait.jpg"
                  alt="Dr. S.J. Vikas, retina and cataract specialist at iNetra Laser Eye & Advanced Retina Centre, Janak Puri, New Delhi"
                  className="relative aspect-[4/5] rounded-t-[999px] rounded-b-[2rem] shadow-2xl shadow-blue-900/25"
                />
                <div className="absolute -left-4 sm:-left-8 top-16 rounded-2xl bg-white shadow-xl shadow-blue-900/10 border border-slate-100 px-4 py-3 animate-float-slow" data-testid="hero-chip-experience">
                  <p className="text-lg font-extrabold text-ink leading-none">10+</p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-1">Years Experience</p>
                </div>
                <div className="absolute -right-3 sm:-right-6 bottom-24 rounded-2xl bg-ink text-white shadow-xl shadow-blue-900/20 px-4 py-3 animate-float-slow" style={{ animationDelay: "1.2s" }} data-testid="hero-chip-surgeries">
                  <p className="text-lg font-extrabold leading-none">10,000+</p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-blue-200 mt-1">Eye Surgeries</p>
                </div>
                <div className="absolute left-1/2 -translate-x-1/2 -bottom-5 rounded-full bg-white shadow-lg border border-slate-100 px-5 py-2.5 flex items-center gap-2 whitespace-nowrap" data-testid="hero-chip-aiims">
                  <Award className="w-4 h-4 text-aqua" aria-hidden="true" />
                  <span className="text-xs font-bold text-ink">AIIMS Trained · New Delhi</span>
                </div>
              </div>
            </FadeIn>
          </motion.div>
        </div>
      </div>
      <div className="relative container-x lg:-mt-10 pb-2">
        <Reveal>
          <div className="rounded-3xl border border-slate-200 bg-white shadow-xl shadow-blue-900/5 overflow-hidden">
            <TrustStrip />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const AboutPreview = () => (
  <section className="py-20 sm:py-28" data-testid="about-preview-section">
    <div className="container-x grid gap-12 lg:grid-cols-2 items-center">
      <Reveal className="relative order-2 lg:order-1">
        <div className="grid grid-cols-2 gap-4">
          <PhotoSlot
            label="Dr. S.J. Vikas"
            note="MBBS, MD, FAICO"
            src="/images/doctor-portrait.jpg"
            alt="Dr. S.J. Vikas — retina, cataract, uvea, ROP and vitreoretinal specialist, New Delhi"
            className="col-span-2 aspect-[16/10] rounded-3xl"
          />
          <PhotoSlot
            label="AIIMS, New Delhi"
            note="MD (Ophthalmology) · December 2017"
            src="/images/aiims-md-certificate.webp"
            fit="contain"
            alt="AIIMS New Delhi MD Ophthalmology degree certificate of Dr. S.J. Vikas, December 2017"
            className="aspect-square rounded-3xl border border-slate-200"
          />
          <PhotoSlot
            label="FAICO — Retina & Vitreous"
            note="All India Collegium of Ophthalmology · June 2021"
            src="/images/faico-certificate.webp"
            fit="contain"
            alt="FAICO fellowship certificate awarded to Dr. S.J. Vikas in Retina and Vitreous, June 2021"
            className="aspect-square rounded-3xl border border-slate-200"
          />
        </div>
      </Reveal>
      <div className="order-1 lg:order-2">
        <SectionHead
          chapter="01"
          kicker="Meet Your Specialist"
          title={<>Meet <span className="font-serif italic font-semibold text-brand-bright">Dr. S.J. Vikas</span></>}
          sub="A renowned name in ophthalmology and eye surgery — with more than 10 years of experience and over 10,000 successful eye surgeries, including complex vitreoretinal and cataract procedures."
        />
        <Reveal delay={0.15} className="mt-8 flex flex-wrap gap-2.5">
          {[DOCTOR.degrees, "AIIMS, New Delhi", "FAICO — Retina & Vitreous", "Retina · Cataract · Uvea · ROP"].map((chip) => (
            <span key={chip} className="rounded-full border border-ink/12 bg-white px-4 py-2 text-xs font-bold text-ink">
              {chip}
            </span>
          ))}
        </Reveal>
        <Reveal delay={0.25} className="mt-8">
          <Link to="/about" className="btn-primary" data-testid="about-preview-button">
            Know Your Doctor <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const TechnologyPreview = () => (
  <section className="py-20 sm:py-28 bg-slate-50" data-testid="technology-preview-section">
    <div className="container-x">
      <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] items-center">
        <div>
          <SectionHead
            chapter="06"
            kicker="Facilities & Technology"
            title="Advanced technology, modern eye care"
            sub="Modern eye care requires accurate diagnosis. iNetra combines specialist clinical expertise with modern ophthalmic diagnostic and examination technology."
          />
          <Reveal delay={0.15} className="mt-8">
            <Link to="/technology" className="btn-primary" data-testid="technology-preview-button">
              Explore Our Technology <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <PhotoSlot
            label="Diagnostic Equipment"
            note=""
            src="/images/equip-kerato-refractometer.webp"
            alt="Huvitz auto kerato-refractometer — diagnostic equipment at iNetra, Janak Puri"
            className="aspect-[4/5] rounded-3xl sm:mt-10"
          />
          <PhotoSlot
            label="Examination Suite"
            note=""
            src="/images/equip-refraction-unit.webp"
            alt="Examination suite with refraction unit and examination chair at iNetra"
            className="aspect-[4/5] rounded-3xl"
          />
        </div>
      </div>
    </div>
  </section>
);

const GallerySection = () => (
  <section className="py-20 sm:py-28" data-testid="gallery-section">
    <div className="container-x">
      <SectionHead
        chapter="07"
        kicker="Clinic Gallery"
        title="Inside iNetra"
        sub="A look at our reception, examination areas and clinical environment in Janak Puri."
      />
      <div className="mt-12">
        <GalleryGrid />
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <AboutPreview />

      <section className="py-20 sm:py-28 bg-slate-50" data-testid="specialties-section">
        <div className="container-x">
          <SectionHead
            chapter="02"
            kicker="Specialties"
            title="Focused expertise across the eye"
            sub="Primary specialisations in retina and cataract, supported by comprehensive ophthalmic care."
          />
          <div className="mt-12">
            <Specialties />
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="retina-section">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead
              chapter="03"
              kicker="Retina Care"
              title="Advanced retina care"
              sub="Comprehensive diagnosis and treatment for complex retinal conditions."
            />
            <Reveal delay={0.1}>
              <Link to="/retina" className="btn-outline" data-testid="retina-explore-button">
                Explore Retina Care <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-12">
            <RetinaServices />
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-slate-50" data-testid="cataract-section">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <SectionHead
              chapter="04"
              kicker="Cataract Care"
              title="Advanced cataract surgery"
              sub="Micro-incisional, sutureless surgery with premium intra-ocular lens options — explained in plain language."
            />
            <Reveal delay={0.1}>
              <Link to="/cataract" className="btn-outline" data-testid="cataract-explore-button">
                Explore Cataract Care <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
          <CataractServices />
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="other-services-section">
        <div className="container-x">
          <SectionHead
            chapter="05"
            kicker="Complete Eye Care"
            title="Complete eye care under one roof"
            sub="Comprehensive ophthalmic evaluation and care for the whole family."
          />
          <div className="mt-12">
            <OtherServices />
          </div>
        </div>
      </section>

      <TechnologyPreview />
      <GallerySection />

      <section className="py-20 sm:py-28 bg-slate-50" data-testid="why-choose-section">
        <div className="container-x">
          <SectionHead
            chapter="08"
            kicker="Why iNetra"
            title="Why patients choose iNetra"
            sub="Specialist expertise, modern technology and a patient-first approach — in the heart of Janak Puri."
          />
          <div className="mt-12">
            <WhyChoose />
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="patient-journey-section">
        <div className="container-x">
          <SectionHead
            chapter="09"
            kicker="Patient Information"
            title="Your eye care, made simple"
            sub="A calm, clear journey — from your first call to your final follow-up."
          />
          <div className="mt-12">
            <PatientJourney />
          </div>
        </div>
      </section>

      <BookSection />
      <MapSection />
    </>
  );
}
